/** @jsxImportSource @emotion/react */
import React, { useCallback, useState } from "react";
import { css, useTheme } from "@emotion/react";
import { Button, Input, ProgressCircle, Text } from "@coreai/component-library";
import { useLanguage } from "LanguageContext";

const MAIL_SAPIENS = "antigreenwashingai@publicis.com";

export default function ContactForm() {
  const { t } = useLanguage();
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  const cardStyles = css`
    display: flex;
    flex-direction: column;
    background-color: ${theme.colors.white};
    padding: ${theme.spacing["4xl"]};
    box-shadow: ${theme.shadows.medium};
    border-radius: ${theme.radius.xs};
  `;

  const formStyles = css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.xl};
    font-family: ${theme.typography.bodyMedium.fontFamily};
    font-size: ${theme.typography.bodyMedium.fontSize};
    font-weight: ${theme.typography.bodyMedium.fontWeight};
    colors: ${theme.colors.purple};
  `;

  const inputRowStyles = css`
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.spacing["4xl"]};
    > div {
      flex: 1;
      min-width: 200px;
    }
  `;

  const buttonContainerStyles = css`
    display: flex;
    justify-content: flex-end;
    margin-top: ${theme.spacing.xl};
  `;

  const bodySemiBoldStyle = {
    fontFamily: theme.typography.body_semiBold.fontFamily,
    fontSize: "20px",
    fontWeight: theme.typography.body_semiBold.fontWeight,
    color: theme.colors.textDark,
    marginTop: theme.spacing.lg,
    marginBottom: theme.spacing.md,
  } as React.CSSProperties;

  const sendEmail = useCallback(() => {
    if (!name || !email || !subject || !message) {
      alert("Please fill in all fields.");
      return;
    }

    // Construct the mailto link
    const mailtoLink = `mailto:${MAIL_SAPIENS}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(
      `Name: ${name}\nEmail: ${email}\n\n${message}`
    )}`;

    // Open the mailto link
    window.location.href = mailtoLink;
  }, [name, email, subject, message]);

  return (
    <div css={cardStyles}>
      <form css={formStyles}>
        <Text style={bodySemiBoldStyle}>{t("contact_form_paragraph")}</Text>
        <div css={inputRowStyles}>
          <Input
            label={t("contact_form_name")}
            onChange={(ev) => setName(ev.target.value)}
            autoComplete="off"
          />
          <Input
            label={t("contact_form_email")}
            onChange={(ev) => setEmail(ev.target.value)}
            autoComplete="off"
          />
        </div>
        <div css={inputRowStyles}>
          <Input
            label={t("contact_form_subject")}
            onChange={(ev) => setSubject(ev.target.value)}
            autoComplete="off"
          />
        </div>
        <div>
          <label className="cai-text-primary cai-text-subbody cai-font-medium peer-disabled:cai-cursor-not-allowed cai-flex cai-flex-1 cai-justify-start cai-text-primary">
            {t("contact_form_message")}
          </label>
          <textarea
            style={{ height: "150px" }}
            className="cai-group cai-flex cai-w-full cai-rounded-md cai-border cai-border-tertiary cai-px-lg cai-py-md cai-text-body2 cai-text-primary cai-placeholder-tertiary hover:cai-border-secondary hover:cai-bg-secondary hover:cai-placeholder-secondary focus:cai-border-brand-primary focus:cai-outline-none focus:cai-ring-2 focus:cai-ring-brand-primary focus:cai-ring-offset-2 disabled:cai-pointer-events-none disabled:cai-cursor-not-allowed cai-bg-primary-invert"
            onChange={(ev) => setMessage(ev.target.value)}
          />
        </div>
        <div css={buttonContainerStyles}>
          <Button
            variant={"brand"}
            icon={isLoading ? "" : "mail"}
            color={theme.colors.primary}
            disabled={
              name === "" ||
              email === "" ||
              subject === "" ||
              message === "" ||
              isLoading
            }
            onClick={() => sendEmail()}
            className="brandPrimaryBtn"
          >
            {isLoading ? (
              <ProgressCircle size="xxs" color={theme.colors.primary} />
            ) : (
              t("core_send")
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
